/**
 * AgentSettingsCard — one agent kind's card on Settings → AI agents: its two default switches,
 * the playbook chip + one-line summary, and the "Edit playbook" link.
 */

import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { AgentDefaultPolicySwitches } from './AgentDefaultPolicySwitches';
import { agentName, isPlaybookCustomized, propertyRulesSummary, summarizePlaybook } from '../../lib/playbook';
import type { AgentPlaybookKindView, PlaybookAgentKind } from '@resida/api-types/agent';

export interface AgentSettingsCardProps {
  agentKind: PlaybookAgentKind;
  /** The saved playbook for this kind; null while loading or when it could not be loaded. */
  view: AgentPlaybookKindView | null;
  playbookLoading: boolean;
}

export function AgentSettingsCard({ agentKind, view, playbookLoading }: AgentSettingsCardProps) {
  const headingId = `agent-settings-card-title-${agentKind}`;

  return (
    <section
      data-testid={`agent-settings-card-${agentKind}`}
      aria-labelledby={headingId}
      className="rounded-lg border border-border bg-card p-[var(--card-padding)]"
    >
      <h2 id={headingId} className="mb-4 text-base font-semibold text-foreground">
        {agentName(agentKind)}
      </h2>

      <AgentDefaultPolicySwitches agentKind={agentKind} />

      <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">Playbook</span>
            {view && (
              <Badge
                variant={isPlaybookCustomized(view) ? 'primary' : 'secondary'}
                data-testid={`agent-playbook-chip-${agentKind}`}
              >
                {isPlaybookCustomized(view) ? 'Playbook customized' : 'Using defaults'}
              </Badge>
            )}
          </div>
          {playbookLoading && <Skeleton className="mt-1.5 h-4 w-48" />}
          {view && (
            <p
              className="mt-0.5 text-sm text-muted-foreground"
              data-testid={`agent-playbook-summary-${agentKind}`}
            >
              {summarizePlaybook(view)}
            </p>
          )}
          {view && propertyRulesSummary(view) && (
            <p
              className="mt-0.5 text-sm text-muted-foreground"
              data-testid={`agent-playbook-property-count-${agentKind}`}
            >
              {propertyRulesSummary(view)}
            </p>
          )}
        </div>
        <Button asChild variant="outline" className="w-full sm:w-auto">
          <Link
            to={`/settings/agents/${agentKind}/playbook`}
            data-testid={`agent-playbook-edit-link-${agentKind}`}
          >
            Edit playbook
          </Link>
        </Button>
      </div>
    </section>
  );
}

export default AgentSettingsCard;
